import React, { useState, useMemo, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Table,
} from "reactstrap";
// Use styling-capable XLSX library; falls back if unavailable
let XLSX;
try {
    // eslint-disable-next-line global-require, import/no-extraneous-dependencies
    XLSX = require('xlsx-js-style');
} catch (e) {
    // eslint-disable-next-line global-require
    XLSX = require('xlsx');
}

// Constants
// Base columns dynamic: 4 normally, 3 when water (oil_name removed)

// Component now expects prop dailyData in same shape as API {success, data:[{machine_id,..., daily:{"1":..}}]}

// Utility functions
const formatDisplayValue = (value) => {
    const numValue = parseFloat(value) || 0;
    return numValue.toString();
};

const transformApiData = (apiData) => {
    // Get the actual number of days from the first item's daily data
    const firstItem = apiData.data[0];
    const actualDaysInMonth = firstItem ? Object.keys(firstItem.daily).length : 31;

    return {
        actualDaysInMonth,
        transformedData: apiData.data.map((item, index) => {
            const transformedItem = {
                srno: index + 1,
                machine_id: item.machine_id,
                machine_name: item.machine_name,
                machine_number: item.machine_number,
                oil_name: item.oil_name,
                oil_price: item.oil_price
            };

            // Add daily data dynamically based on actual days in API response
            for (let day = 1; day <= actualDaysInMonth; day++) {
                transformedItem[`day${day}`] = item.daily[day.toString()] || 0;
            }

            // Calculate total quantity for this row
            let totalQty = 0;
            for (let day = 1; day <= actualDaysInMonth; day++) {
                totalQty += parseFloat(transformedItem[`day${day}`]) || 0;
            }
            transformedItem.totalQty = totalQty;
            // Calculate total value for this row (qty * price)
            const price = parseFloat(item.oil_price) || 0;
            transformedItem.totalValue = totalQty * price;

            return transformedItem;
        })
    };
};

const ReportDayWiseView = ({ onBack, dailyData, oilType, month, companyName }) => {
    const searchTerm = ""; // search disabled
    const [reportData, setReportData] = useState([]);
    const [actualDaysInMonth, setActualDaysInMonth] = useState(31);

    useEffect(() => {
        if (dailyData?.data) {
            const result = transformApiData(dailyData);
            setReportData(result.transformedData);
            setActualDaysInMonth(result.actualDaysInMonth);
        }
    }, [dailyData]);

    // Capitalize first letter of oilType for display
    const displayOilType = useMemo(() => {
        if (!oilType) return '';
        const base = oilType === 'water' ? 'Water' : oilType.charAt(0).toUpperCase() + oilType.slice(1);
        return base;
    }, [oilType]);

    // Create dynamic day indices based on actual days
    const dayIndices = useMemo(() => {
        return Array.from({ length: actualDaysInMonth }, (_, i) => i + 1);
    }, [actualDaysInMonth]);

    // Derive month name from month prop (supports number 1-12, "08", "2025-08", "08-2025")
    const displayMonth = useMemo(() => {
        if (month === undefined || month === null || month === '') return '';
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const tryIndex = (m) => (m >= 1 && m <= 12) ? monthNames[m - 1] : null;
        if (typeof month === 'number') return tryIndex(month) || month.toString();
        if (typeof month === 'string') {
            const trimmed = month.trim();
            if (/^\d{1,2}$/.test(trimmed)) {
                const num = parseInt(trimmed, 10);
                const named = tryIndex(num);
                if (named) return named;
            }
            // Patterns like YYYY-MM or MM-YYYY
            let match = trimmed.match(/^(\d{4})-(\d{1,2})$/); // YYYY-MM
            if (match) {
                const named = tryIndex(parseInt(match[2], 10));
                if (named) return named + ' ' + match[1];
            }
            match = trimmed.match(/^(\d{1,2})-(\d{4})$/); // MM-YYYY
            if (match) {
                const named = tryIndex(parseInt(match[1], 10));
                if (named) return named + ' ' + match[2];
            }
            // Try Date parse
            const d = new Date(trimmed + (trimmed.length === 7 ? '' : '-01'));
            if (!isNaN(d)) return monthNames[d.getMonth()] + ' ' + d.getFullYear();
            return trimmed; // fallback
        }
        return month.toString();
    }, [month]);

    // Compute real days in the provided month prop (fallback 31 to keep table width consistent)
    const daysInSelectedMonth = useMemo(() => {
        if (month === undefined || month === null || month === '') return 31;
        const currentYear = new Date().getFullYear();
        let m = null; let y = currentYear;
        if (typeof month === 'number') {
            m = month;
        } else if (typeof month === 'string') {
            const trimmed = month.trim();
            if (/^\d{1,2}$/.test(trimmed)) {
                m = parseInt(trimmed, 10);
            } else {
                let match = trimmed.match(/^(\d{4})-(\d{1,2})$/); // YYYY-MM
                if (match) { y = parseInt(match[1], 10); m = parseInt(match[2], 10); }
                else if ((match = trimmed.match(/^(\d{1,2})-(\d{4})$/))) { // MM-YYYY
                    m = parseInt(match[1], 10); y = parseInt(match[2], 10);
                } else {
                    const d = new Date(trimmed);
                    if (!isNaN(d)) { y = d.getFullYear(); m = d.getMonth() + 1; }
                }
            }
        }
        if (!m || m < 1 || m > 12) return 31;
        return new Date(y, m, 0).getDate();
    }, [month]);

    // Memoized filtered data
    const filteredData = useMemo(() => {
        if (!searchTerm) return reportData;
        return reportData.filter(item => {
            const searchFields = [item.oil_name, item.machine_name, item.srno.toString()];
            return searchFields.some(field =>
                field?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
        });
    }, [searchTerm, reportData]);

    // Memoized column totals
    // Precompute totals (quantity & value) in a single pass for all days
    const { qtyTotals, valueTotals } = useMemo(() => {
        const qtyTotals = new Array(actualDaysInMonth).fill(0);
        const valueTotals = new Array(actualDaysInMonth).fill(0);
        if (!filteredData.length) return { qtyTotals, valueTotals };
        for (let i = 0; i < filteredData.length; i++) {
            const item = filteredData[i];
            const price = parseFloat(item.oil_price) || 0;
            for (let d = 0; d < actualDaysInMonth; d++) {
                const day = d + 1;
                const v = parseFloat(item[`day${day}`]) || 0;
                if (v) { // skip zeros
                    qtyTotals[d] += v;
                    if (price) valueTotals[d] += v * price;
                }
            }
        }
        return { qtyTotals, valueTotals };
    }, [filteredData, actualDaysInMonth]);

    // Event handlers - removed edit and delete functionality

    // Table headers configuration
    const tableHeaders = useMemo(() => {
        const headers = [
            { key: 'srno', label: 'SN', minWidth: '50px', align: 'center' },
        ];
        if (oilType !== 'water') {
            headers.push({ key: 'oil_name', label: 'Oil Name', minWidth: '120px', align: 'left' });
        }
        headers.push(
            { key: 'machine_name', label: 'Machine Name', minWidth: '130px', align: 'left' },
            { key: 'machine_number', label: 'Machine No', minWidth: '140px', align: 'left' },
        );

        // Add day headers dynamically based on actual days
        for (let day = 1; day <= actualDaysInMonth; day++) {
            headers.push({
                key: `day${day}`,
                label: 'Topup',
                minWidth: '40px',
                align: 'center'
            });
        }

        // Add Total Qty and Total Value columns
        headers.push(
            { key: 'totalQty', label: 'Total Qty', minWidth: '80px', align: 'center' },
            { key: 'totalValue', label: 'Total Value', minWidth: '90px', align: 'center' }
        );

        return headers;
    }, [oilType, actualDaysInMonth]);

    // Sticky column configuration (first 4 columns)
    const STICKY_CONFIG = useMemo(() => {
        if (oilType === 'water') {
            return [
                { key: 'srno', left: 0, width: 50 },
                { key: 'machine_name', left: 50, width: 130 },
                { key: 'machine_number', left: 180, width: 140 },
            ];
        }
        return [
            { key: 'srno', left: 0, width: 50 },
            { key: 'oil_name', left: 50, width: 120 },
            { key: 'machine_name', left: 170, width: 130 },
            { key: 'machine_number', left: 300, width: 140 }
        ];
    }, [oilType]);

    const STICKY_TOTAL_WIDTH = useMemo(() => STICKY_CONFIG.reduce((sum, c) => sum + c.width, 0), [STICKY_CONFIG]);

    const stickyStyleFor = (key, isHeader = false) => {
        const cfg = STICKY_CONFIG.find(c => c.key === key);
        console.log('Sticky style for', key, cfg);
        if (!cfg) return {};
        return {
            position: 'sticky',
            left: cfg.left,
            minWidth: cfg.width,
            maxWidth: cfg.width,
            width: cfg.width,
            background: isHeader ? '#f8f9fa' : '#fff',
            zIndex: isHeader ? 15 : 10,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            borderLeft: '1px solid #dee2e6',
            borderRight: '1px solid #dee2e6',
            borderTop: '1px solid #dee2e6',
            borderBottom: '1px solid #dee2e6',
        };
    };

    const stickySpanCellStyle = (isHeader = false) => ({
        position: 'sticky',
        left: 0,
        minWidth: STICKY_TOTAL_WIDTH,
        width: STICKY_TOTAL_WIDTH,
        maxWidth: STICKY_TOTAL_WIDTH,
        background: isHeader ? '#f8f9fa' : '#fff',
        zIndex: isHeader ? 16 : 11,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        // Add proper borders to maintain visibility during scroll
        borderLeft: '1px solid #dee2e6',
        borderRight: '1px solid #dee2e6',
        borderTop: '1px solid #dee2e6',
        borderBottom: '1px solid #dee2e6',
    });

    // Render functions
    const renderTableHeaders = () => (
        <>
            {/* Top row with date and numbered columns */}
            <tr className="bg-blue-50">
                <th
                    colSpan={oilType === 'water' ? 3 : 4}
                    className="text-center text-xs font-semibold py-2 px-3 bg-blue-100"
                    style={{
                        ...stickySpanCellStyle(true),
                        borderBottom: '1px solid #dee2e6'
                    }}
                >
                    <u>Reported month - {displayMonth || month}</u>
                </th>
                {/* Numbered columns based on actual days */}
                {dayIndices.map(day => (
                    <th
                        key={`num-${day}`}
                        className="text-center text-xs font-semibold py-2 px-3 bg-blue-50"
                        style={{
                            minWidth: '40px',
                            borderBottom: '1px solid #dee2e6',
                            borderRight: '1px solid #dee2e6',
                            borderLeft: '1px solid #dee2e6'
                        }}
                    >
                        {day}
                    </th>
                ))}
                {/* Total columns headers */}
                <th
                    className="text-center text-xs font-semibold py-2 px-3 bg-blue-100"
                    style={{
                        minWidth: '80px',
                        borderBottom: '1px solid #dee2e6',
                        borderRight: '1px solid #dee2e6',
                        borderLeft: '1px solid #dee2e6'
                    }}
                >
                    {/* Total Qty */}
                </th>
                <th
                    className="text-center text-xs font-semibold py-2 px-3 bg-green-100"
                    style={{
                        minWidth: '90px',
                        borderBottom: '1px solid #dee2e6',
                        borderRight: '1px solid #dee2e6',
                        borderLeft: '1px solid #dee2e6'
                    }}
                >
                    {/* Total Value */}
                </th>
            </tr>
            {/* Original header row */}
            <tr>
                {tableHeaders.map((header) => {
                    const stickyStyle = stickyStyleFor(header.key, true);
                    return (
                        <th
                            key={header.key}
                            className={`text-${header.align} text-xs font-semibold py-3 px-3`}
                            style={{
                                minWidth: header.minWidth,
                                ...stickyStyle,
                                borderBottom: '1px solid #dee2e6'
                            }}
                        >
                            {header.label}
                        </th>
                    );
                })}
            </tr>
        </>
    );

    const renderDataRow = (item) => (
        <tr key={item.srno} className="table-row-hover">
            <td
                className="text-center text-xs align-middle py-2 px-2"
                style={{
                    ...stickyStyleFor('srno'),
                    borderBottom: '1px solid #dee2e6'
                }}
            >
                {item.srno}
            </td>
            {oilType !== 'water' && (
                <td
                    className="text-left text-xs align-middle py-2 px-2"
                    style={{
                        ...stickyStyleFor('oil_name'),
                        borderBottom: '1px solid #dee2e6'
                    }}
                >
                    {item.oil_name}
                </td>
            )}
            <td
                className="text-left text-xs align-middle py-2 px-2"
                style={{
                    ...stickyStyleFor('machine_name'),
                    borderBottom: '1px solid #dee2e6'
                }}
            >
                {item.machine_name}
            </td>
            <td
                className="text-left text-xs align-middle py-2 px-2"
                style={{
                    ...stickyStyleFor('machine_number'),
                    borderBottom: '1px solid #dee2e6'
                }}
            >
                {item.machine_number}
            </td>

            {/* Render daily values dynamically */}
            {dayIndices.map(day => {
                const dayKey = `day${day}`;
                return (
                    <td
                        key={dayKey}
                        className="text-center text-xs align-middle py-2.5 px-1"
                        style={{
                            borderBottom: '1px solid #dee2e6',
                            borderRight: '1px solid #dee2e6',
                            borderLeft: '1px solid #dee2e6'
                        }}
                    >
                        {formatDisplayValue(item[dayKey])}
                    </td>
                );
            })}

            {/* Total Qty column */}
            <td
                className="text-center text-xs align-middle py-2.5 px-1 font-semibold bg-blue-50"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {(item.totalQty || 0).toFixed(2)}
            </td>

            {/* Total Value column */}
            <td
                className="text-center text-xs align-middle py-2.5 px-1 font-semibold bg-green-50"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {(item.totalValue || 0).toFixed(2)}
            </td>
        </tr>
    );

    const renderTotalRows = () => [
        // Total Qty row
        <tr key="totals-qty" className="bg-gray-50">
            <td
                className="text-center text-xs align-middle py-2.5 px-1"
                colSpan={oilType === 'water' ? 3 : 4}
                style={{
                    ...stickySpanCellStyle(),
                    borderBottom: '1px solid #dee2e6',
                    borderTop: '2px solid #dee2e6'
                }}
            >
                Total Qty
            </td>

            {/* Render daily totals dynamically */}
            {qtyTotals.map((v, idx) => (
                <td
                    key={`total-qty-day${idx + 1}`}
                    className="text-center text-xs align-middle py-2.5 px-1"
                    style={{
                        borderBottom: '1px solid #dee2e6',
                        borderTop: '2px solid #dee2e6',
                        borderRight: '1px solid #dee2e6',
                        borderLeft: '1px solid #dee2e6'
                    }}
                >
                    {v.toFixed(2)}
                </td>
            ))}

            {/* Grand Total Qty */}
            <td
                className="text-center text-xs align-middle py-2.5 px-1 font-bold bg-blue-100"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderTop: '2px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {qtyTotals.reduce((sum, val) => sum + val, 0).toFixed(2)}
            </td>

            {/* Grand Total Value */}
            <td
                className="text-center text-xs align-middle py-2.5 px-1 font-bold bg-green-100"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderTop: '2px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {valueTotals.reduce((sum, val) => sum + val, 0).toFixed(2)}
            </td>
        </tr>,

        // Total Value row
        <tr key="totals-value" className="bg-gray-50">
            <td
                className="text-center text-xs align-middle py-2.5 px-2"
                colSpan={oilType === 'water' ? 3 : 4}
                style={{
                    ...stickySpanCellStyle(),
                    borderBottom: '1px solid #dee2e6'
                }}
            >
                Total Value
            </td>

            {/* Render daily value totals (calculated from qty * price) */}
            {valueTotals.map((v, idx) => (
                <td
                    key={`total-value-day${idx + 1}`}
                    className="text-center text-xs align-middle py-2.5 px-2"
                    style={{
                        borderBottom: '1px solid #dee2e6',
                        borderRight: '1px solid #dee2e6',
                        borderLeft: '1px solid #dee2e6'
                    }}
                >
                    {v.toFixed(2)}
                </td>
            ))}

            {/* Grand Total Qty (for this row, same as above) */}
            <td
                className="text-center text-xs align-middle py-2.5 px-2 font-bold bg-blue-100"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {qtyTotals.reduce((sum, val) => sum + val, 0).toFixed(2)}
            </td>

            {/* Grand Total Value */}
            <td
                className="text-center text-xs align-middle py-2.5 px-2 font-bold bg-green-100"
                style={{
                    borderBottom: '1px solid #dee2e6',
                    borderRight: '1px solid #dee2e6',
                    borderLeft: '1px solid #dee2e6'
                }}
            >
                {valueTotals.reduce((sum, val) => sum + val, 0).toFixed(2)}
            </td>
        </tr>
    ];

    const renderTableRows = () => {
        if (filteredData.length === 0) {
            const baseCols = oilType === 'water' ? 3 : 4;
            const totalColsDynamic = baseCols + actualDaysInMonth + 2; // base cols + days + total qty + total value
            return (
                <tr>
                    <td colSpan={totalColsDynamic} className="text-center py-4 text-gray-500">
                        <i className="fa fa-search fa-2x mb-2 text-muted"></i>
                        <div>No records found matching "{searchTerm}"</div>
                    </td>
                </tr>
            );
        }

        return [
            ...filteredData.map(renderDataRow),
            ...renderTotalRows()
        ];
    };

    // Download function for Excel/CSV export
    const downloadReport = (data, format = 'xlsx') => {
        try {
            // Prepare data for export exactly matching the screenshot format
            const exportData = [];

            // Add title row - exactly as shown in screenshot
            const titleRow = [`Monthly daywise Data Report ${displayOilType} ${oilType === 'water' ? '' : 'Oil'}`];
            exportData.push(titleRow); // Row 1

            // Add company details row (merged later)
            if (companyName) {
                exportData.push([`Company: ${companyName}`]); // Row 2
            } else {
                exportData.push([`Company: -`]); // ensure consistent row index
            }

            // Empty spacer row
            exportData.push([]); // Row 3

            // Add Date row header with colspan of 4 (will now be row 4)
            const baseColspan = oilType === 'water' ? 3 : 4;
            const dateHeaderRow = [`Date:- 1-${daysInSelectedMonth} (${displayMonth})`];
            for (let i = 1; i < baseColspan; i++) dateHeaderRow.push('');
            // Add numbered columns 1 through actual days in month
            for (let day = 1; day <= actualDaysInMonth; day++) {
                dateHeaderRow.push(day);
            }
            exportData.push(dateHeaderRow);

            // Add main headers row exactly as shown in screenshot
            const mainHeaders = ['S.no'];
            if (oilType !== 'water') mainHeaders.push('Oil Name');
            mainHeaders.push('Machine Name', 'Machine Number');
            // Add "Topup" for each day column
            for (let day = 1; day <= actualDaysInMonth; day++) {
                mainHeaders.push('Topup');
            }
            // Add Total Qty and Total Value headers
            mainHeaders.push('Total Qty', 'Total Value');
            exportData.push(mainHeaders);

            // Add data rows exactly matching the screenshot format
            data.forEach(item => {
                const row = [item.srno];
                if (oilType !== 'water') row.push(item.oil_name);
                row.push(item.machine_name, item.machine_number);

                // Add daily values (actual days in month)
                for (let day = 1; day <= actualDaysInMonth; day++) {
                    const dayKey = `day${day}`;
                    const value = parseFloat(item[dayKey]) || 0;
                    row.push(value); // Show 0 instead of empty cell to match UI
                }

                // Add Total Qty and Total Value for this row
                row.push((item.totalQty || 0).toFixed(2));
                row.push((item.totalValue || 0).toFixed(2));

                exportData.push(row);
            });

            // Add Total Qty row
            const totalQtyRow = ['Total Qty'];
            for (let i = 1; i < baseColspan; i++) totalQtyRow.push('');
            for (let d = 0; d < actualDaysInMonth; d++) totalQtyRow.push((qtyTotals[d] || 0).toFixed(2));
            // Add grand totals for Total Qty row
            totalQtyRow.push(qtyTotals.reduce((sum, val) => sum + val, 0).toFixed(2)); // Grand Total Qty
            totalQtyRow.push(valueTotals.reduce((sum, val) => sum + val, 0).toFixed(2)); // Grand Total Value
            exportData.push(totalQtyRow);

            // Add Total Value row
            const totalValueRow = ['Total Value'];
            for (let i = 1; i < baseColspan; i++) totalValueRow.push('');
            for (let d = 0; d < actualDaysInMonth; d++) totalValueRow.push((valueTotals[d] || 0).toFixed(2));
            // Add grand totals for Total Value row (same values as Total Qty row)
            totalValueRow.push(qtyTotals.reduce((sum, val) => sum + val, 0).toFixed(2)); // Grand Total Qty
            totalValueRow.push(valueTotals.reduce((sum, val) => sum + val, 0).toFixed(2)); // Grand Total Value
            exportData.push(totalValueRow);

            // Create workbook and worksheet
            const ws = XLSX.utils.aoa_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Daily Report");

            // Merge title & company rows across all columns (A1..AJ1 & A2..AJ2). Dynamic columns total
            // Build dynamic merges (title, company, and total rows)
            const totalColumns = 1 + (oilType === 'water' ? 2 : 3) + actualDaysInMonth + 2; // s.no + (oil_name?) + machine + number + days + total_qty + total_value
            const lastCol = totalColumns - 1; // zero based index
            const merges = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } }, // Title row
                { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } }, // Company row
                { s: { r: 3, c: 0 }, e: { r: 3, c: baseColspan - 1 } }, // Date header row first N columns merged
            ];

            // Row indices (0-based) after pushing data rows:
            // 0: title, 1: company, 2: empty, 3: date header, 4: main headers, 5..(5+data.length-1): data rows
            const totalQtyRowIndex = 5 + data.length;      // Total Qty row index
            const totalValueRowIndex = totalQtyRowIndex + 1; // Total Value row index

            // Merge first 4 columns for Total Qty & Total Value labels to mimic UI colspan=4
            merges.push({ s: { r: totalQtyRowIndex, c: 0 }, e: { r: totalQtyRowIndex, c: baseColspan - 1 } });
            merges.push({ s: { r: totalValueRowIndex, c: 0 }, e: { r: totalValueRowIndex, c: baseColspan - 1 } });

            ws['!merges'] = merges;

            // Set column widths to match screenshot
            const colWidths = [
                { wch: 5 },   // Sno
            ];
            if (oilType !== 'water') colWidths.push({ wch: 14 }); // Oil Name
            colWidths.push({ wch: 16 }); // Machine Name
            colWidths.push({ wch: 18 }); // Machine Number

            // Add widths for day columns (actual days in month)
            for (let day = 1; day <= actualDaysInMonth; day++) {
                colWidths.push({ wch: 6 });
            }

            // Add widths for Total Qty and Total Value columns
            colWidths.push({ wch: 10 }); // Total Qty
            colWidths.push({ wch: 12 }); // Total Value

            ws['!cols'] = colWidths;

            // Style the title row to be centered and bold
            if (ws['A1']) ws['A1'].s = { font: { bold: false, sz: 12 }, alignment: { horizontal: 'center', vertical: 'center' } };
            if (ws['A2']) ws['A2'].s = { font: { bold: true, sz: 12 }, alignment: { horizontal: 'center', vertical: 'center' } };
            // Date header now at row 4 => A4
            if (ws['A4']) ws['A4'].s = { font: { bold: true, sz: 11 }, alignment: { horizontal: 'center', vertical: 'center' } };
            // if (ws['A7']) ws['A7'].s = { font: { bold: true, sz: 11 }, alignment: { horizontal: 'center', vertical: 'center' } };
            // if (ws['A8']) ws['A8'].s = { font: { bold: true, sz: 11 }, alignment: { horizontal: 'center', vertical: 'center' } };
            // Generate filename with current date
            const currentDate = new Date();
            const dateStr = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD format
            const filename = `Daily_${displayOilType}_${oilType === 'water' ? 'Consumption' : 'Oil_Report'}_${dateStr}`;

            // Download file
            if (format === 'csv') {
                XLSX.writeFile(wb, `${filename}.csv`);
            } else {
                XLSX.writeFile(wb, `${filename}.xlsx`);
            }

            console.log(`Report downloaded successfully as ${filename}.${format}`);
        } catch (error) {
            console.error('Error downloading report:', error);
            alert('Failed to download report. Please try again.');
        }
    };


    return (
        <Container fluid className="mt-4">
            <Row>
                <Col xl="12">
                    <Card className="shadow border-0">
                        <CardBody className="bg-gray-50">
                            {!dailyData && (
                                <div className="text-center py-5 text-muted">Loading...</div>
                            )}
                            {/* Stats Section */}
                            <Row className="mb-3">
                                <Col lg="6">
                                    <div className="text-sm text-gray-500">
                                        <Button
                                            color="link"
                                            onClick={onBack}
                                            className="p-0 text-xs no-underline"
                                        >
                                            <i className="fa fa-arrow-left mr-2"></i>
                                            Back
                                        </Button>
                                        <i className="fa fa-table mr-1"></i>
                                        Daily {displayOilType} Consumption Report - {filteredData.length} records
                                    </div>
                                </Col>
                                <Col lg="6" className="text-right">

                                    <div
                                        onClick={() => downloadReport(filteredData, 'xlsx')}
                                        style={{
                                            padding: '2px 4px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer',
                                            color: '#418efc',
                                            borderBottom: '1.5px solid #418efc',
                                        }}
                                    >
                                        <i className="fa-regular fa-circle-down" style={{ fontSize: '13px' }}></i>
                                        Download Excel
                                    </div>
                                </Col>
                            </Row>

                            {/* Table Section */}
                            <Row>
                                <Col xs="12">
                                    <div
                                        className="sticky-table-wrapper"
                                        style={{
                                            maxHeight: 'calc(100vh - 200px)', // Limit height to enable vertical scroll
                                            overflowY: 'auto',
                                            overflowX: 'auto'
                                        }}
                                    >
                                        <Table
                                            className="table-hover table-flush text-sm bg-white"
                                            style={{
                                                borderCollapse: 'separate',
                                                borderSpacing: 0,
                                                border: 'none',
                                                margin: 0
                                            }}
                                        >
                                            <thead
                                                className="thead-light bg-gray-50"
                                                style={{
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 20,
                                                    borderBottom: '2px solid #dee2e6'
                                                }}
                                            >
                                                {renderTableHeaders()}
                                            </thead>
                                            <tbody>
                                                {renderTableRows()}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ReportDayWiseView;